// How old is what you are looking at. P6-83.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 FRESHNESS IS NOT A FOOTNOTE — SPEC §6 MAKES IT A PROPERTY OF THE MODULE
// ═══════════════════════════════════════════════════════════════════════════
//
// *"The portal shows as-of on projected data (the reconcile stamp). A member
// seeing last week's rating with no date will report a bug that is actually a
// label."* Every projected module on this page carries a stamp built here.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 UTC, EXPLICITLY — AND THIS IS THE SITE'S EXISTING RULE, NOT A NEW ONE
// ═══════════════════════════════════════════════════════════════════════════
//
// `src/directory.js::asOfLabel` fixes the zone and says why: the stamp is an
// INSTANT, and rendering it in the reader's zone gives six people six different
// answers to "how old is this". This file keeps that and adds the relative age,
// which is the half that actually answers the question a member is asking.
//
// ⚠️ THE FORMAT IS SHORTER THAN directory.js's ON PURPOSE, AND IT IS A LAYOUT
// DECISION. The handoff's two measured 375px overflow traps were a column
// without `box-sizing:border-box` and a header row with two `nowrap` children,
// and its instruction is explicit: *"keep the as-of stamp nowrap and let
// headings shorten."* A stamp that cannot wrap has to be short, so this renders
// `As of 02:53 UTC · 6 min ago` and not `22 Aug 2026, 02:53 UTC`. The full ISO
// instant is in `title`, which costs no width at all.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 NO DATE AVAILABLE → SAY SO IN WORDS. NEVER A STAMP WITH NOTHING BEHIND IT
// ═══════════════════════════════════════════════════════════════════════════
//
// An empty stamp, or one rendering "Invalid Date", is worse than no stamp: it
// looks like a freshness guarantee and is not one. `UNKNOWN` is a sentence, and
// the caller renders it in the same slot so the absence is as visible as the
// presence would have been.

const CLOCK = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: 'UTC',
})

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 THE BLOB CARRIES TWO KINDS OF TIMESTAMP AND THEY MUST NOT BE READ ALIKE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Measured in the bot's own source, and it cost this file a wrong answer before
 * it was:
 *
 *   as_of              `datetime.now(timezone.utc).isoformat()`  →  AWARE,
 *                      e.g. `2026-09-03T19:04:00.000000+00:00`. A real instant.
 *   finalized_at       `datetime.now().isoformat()`              →  NAIVE,
 *   ratings_finalized_at   e.g. `2026-08-27T22:10:00`. A wall clock with no
 *                      zone attached to it at all.
 *
 * ⚠️ JavaScript parses a bare date-time as the BROWSER'S local time. So
 * `new Date('2026-08-27T22:10:00')` formatted with `timeZone: 'UTC'` applies the
 * reader's offset to a string that never had one — and a night finalized at
 * 22:10 renders as the 28th for anybody east of Greenwich. Caught by a test that
 * expected "27 Aug" and got "28 Aug" on the dev machine; CI, on UTC, would have
 * agreed with the code and shipped it.
 *
 * 🔴 THE RULE: a stamp WITH a zone designator is an instant and is converted; a
 * stamp WITHOUT one is a wall clock and its own fields ARE the answer, converted
 * by nothing. This is CLAUDE.md gotcha 21's seam — two implementations of one
 * contract — and the half that is not ours is Python's `isoformat()`.
 */
const ZONED = /(?:Z|[+-]\d{2}:?\d{2})$/
const FIELDS = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/

/** What a module says when its projection carries no usable stamp. */
export const UNKNOWN_AS_OF = 'No date on this'

/**
 * 🔴 TRIPS GETS THIS INSTEAD OF A STAMP, AND THE DISTINCTION IS THE POINT.
 *
 * The trip panel is a direct read of the trip service — not a projection, not
 * published, not reconciled — so it is as current as the request that fetched
 * it. An as-of stamp there would claim a staleness it does not have, which is
 * the same class of error as the missing stamp it is meant to prevent: a label
 * that is wrong about how much to trust what is under it.
 */
export const LIVE_LABEL = 'Live'

/** How long ago, in the fewest characters that are still true. */
function ago(deltaMs) {
  if (deltaMs < 0) {
    // A stamp from the future is a clock disagreement, not a freshness fact.
    // Saying "in 3 minutes" would be nonsense; saying "just now" would be a
    // claim. It gets named.
    return 'clock ahead'
  }
  if (deltaMs < MINUTE) return 'just now'
  if (deltaMs < HOUR) return `${Math.floor(deltaMs / MINUTE)} min ago`
  if (deltaMs < DAY) return `${Math.floor(deltaMs / HOUR)} h ago`
  return `${Math.floor(deltaMs / DAY)} d ago`
}

/**
 * The stamp for one projected module.
 *
 * `nowMs` is a parameter rather than a `Date.now()` inside, so a test can assert
 * the wording of every band without waiting for one — and so the whole page
 * renders one consistent "now" instead of each module sampling its own.
 *
 * Returns `{ known, label, title }`. `title` is the ISO value verbatim, which is
 * the thing an operator actually needs and the thing a relative label destroys.
 */
export function freshness(iso, nowMs) {
  if (typeof iso !== 'string' || iso.trim() === '') {
    return { known: false, label: UNKNOWN_AS_OF, title: null }
  }
  // 🔴 "HOW LONG AGO" IS ARITHMETIC ON AN INSTANT, AND A NAIVE STAMP IS NOT ONE.
  // Assuming a zone would produce a confident age that is wrong by the reader's
  // offset — a label that is wrong about how much to trust what is under it,
  // which is the failure this whole module exists to prevent. The publisher
  // writes `as_of` with `timezone.utc` and always has, so this branch is
  // defensive; it refuses rather than guessing because a wrong age looks exactly
  // like a right one.
  if (!ZONED.test(iso.trim())) {
    return { known: false, label: UNKNOWN_AS_OF, title: null }
  }
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) {
    return { known: false, label: UNKNOWN_AS_OF, title: null }
  }
  const now = Number.isFinite(nowMs) ? nowMs : Date.now()
  return {
    known: true,
    label: `As of ${CLOCK.format(parsed)} UTC · ${ago(now - parsed.getTime())}`,
    title: iso,
  }
}

/**
 * A published instant as a short calendar date, or `null`.
 *
 * ⚠️ THE SITE DOES NOT DERIVE A NIGHT'S DATE FROM ITS `week` KEY, AND MUST NOT.
 * `utils/projection_publisher.py` states the rule and the reason: the manager
 * owns that arithmetic (`%Y-W%U` Sunday-start plus the guild's
 * `movie_night_day`), the guild's movie-night day is NOT published, and *"a
 * second implementation would drift the way every duplicated derivation in this
 * repo has"*. So a night is dated by a real timestamp the store already holds —
 * `finalized_at`, falling back to `ratings_finalized_at` — or it is not dated at
 * all, and the page says which.
 */
export function shortDate(iso) {
  const ms = instantOf(iso)
  return ms === null ? null : DATE.format(new Date(ms))
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 THE ONE PLACE A PUBLISHED STAMP BECOMES A NUMBER — P6-182, P6-167
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * The activity feed orders events from TWO producers that stamp differently,
 * measured by running both publishers rather than read off their source:
 *
 *   3a/movie.json   finalized_at        `2026-08-27T22:10:00`        NAIVE
 *   3a/music.json   recent_events[].at  `2026-09-02T10:17:14+00:00`  AWARE
 *
 * So "which of these happened first" is a real question and it cannot be
 * answered by `Date.parse` on both — JavaScript reads the naive one as the
 * BROWSER'S local time, and the two rows would swap places for a reader in a
 * different zone while agreeing on a UTC runner. That is P6-167's instrument
 * problem exactly: CI shares the bug's assumption and reports agreement.
 *
 * ✅ THE DECISION, WRITTEN NEXT TO THE CODE THAT MAKES IT, because P6-167 asks
 * for that in as many words: **a naive stamp is read as UTC.** Not because
 * naive means UTC in general — it does not — but because the process that
 * WROTE it runs UTC (Railway's container, and `utils/projection_publisher.py`
 * already does `parsed.replace(tzinfo=timezone.utc)` on the same values), so
 * UTC is the writer's own wall clock rather than a guess about the reader's.
 * `shortDate` has made this choice since P6-83; this is the same choice, named
 * and shared, so ordering and rendering cannot drift apart.
 *
 * ⚠️ WHAT IT COSTS, SAID OUT LOUD: if the store's clock ever stops being UTC,
 * every row here is wrong by that offset — at most a day's ordering error
 * between two events less than a zone apart. The fix is at the WRITE side
 * (P6-167), and when it lands these stamps arrive zoned and take the branch
 * above instead. Nothing here has to change for that.
 *
 * Returns epoch milliseconds, or `null` for anything that is not a date this
 * repo writes. `null` is what makes an undated event droppable rather than
 * sortable-as-now.
 */
export function instantOf(iso) {
  if (typeof iso !== 'string' || iso.trim() === '') return null
  const value = iso.trim()
  const fields = FIELDS.exec(value)
  if (fields === null) return null

  if (ZONED.test(value)) {
    // An instant. Convert it, in UTC, for the reason at the top of this file.
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? null : parsed.getTime()
  }

  // A wall clock. Its own fields ARE the answer, so they are put into a UTC date
  // and read back as UTC — which converts by nothing, whatever zone the reader
  // is in. See the note above: this is the branch a naive `finalized_at` takes,
  // and getting it wrong moves a night onto the wrong day for half the world.
  const year = Number(fields[1])
  const month = Number(fields[2])
  const day = Number(fields[3])
  // The time is optional — `2026-08-27` is a stamp the store can write — and a
  // missing one is midnight rather than a refusal. It is READ, though, and it
  // was not before: a feed orders two events on the same day, and a date that
  // threw its clock away would sort them by nothing.
  const hour = fields[4] === undefined ? 0 : Number(fields[4])
  const minute = fields[5] === undefined ? 0 : Number(fields[5])
  const built = new Date(Date.UTC(year, month - 1, day, hour, minute))
  if (Number.isNaN(built.getTime())) return null
  // `Date.UTC` ROLLS OVER rather than refusing — `2026-02-31` becomes 3 March —
  // so an impossible date must be caught by reading the result back. Without
  // this, a corrupt stamp renders as a plausible day nobody can trace.
  if (built.getUTCFullYear() !== year || built.getUTCMonth() !== month - 1 || built.getUTCDate() !== day) {
    return null
  }
  return built.getTime()
}

/**
 * When an event happened, in the fewest characters a feed row can spare.
 *
 * 🔴 IT LIVES HERE RATHER THAN IN `activity.js` BECAUSE THIS FILE ALREADY OWNS
 * "WHEN" ON THIS PAGE. P6-172 is the record of what two derivations of one
 * concept cost — two answers to a member's average, differing by 2.5 — and
 * P6-177 is the same lesson about a WORD. A feed that formatted its own dates
 * would be a third spelling of a fact the panel beside it already renders.
 *
 * The bands are the design's: `2h`, `Mon`, `27 Aug`. `null` when the stamp is
 * not one we can read, so the caller drops the row rather than printing an
 * empty cell — an undated event sorts nowhere and reads as *"just now"*, which
 * is the publisher's own reason for refusing to emit one.
 *
 * ⚠️ A FUTURE STAMP GETS ITS DATE, NOT A RELATIVE AGE. `freshness()` names a
 * clock disagreement rather than inventing a tense for it; a feed row has no
 * space for that sentence, so it falls back to the one thing the stamp still
 * says truthfully — which day it claims to be.
 */
const WEEKDAY = new Intl.DateTimeFormat('en-GB', { weekday: 'short', timeZone: 'UTC' })

export function sinceLabel(iso, nowMs) {
  const ms = instantOf(iso)
  if (ms === null) return null
  const now = Number.isFinite(nowMs) ? nowMs : Date.now()
  const delta = now - ms
  if (delta < 0) return shortDate(iso)
  if (delta < MINUTE) return 'now'
  if (delta < HOUR) return `${Math.floor(delta / MINUTE)}m`
  if (delta < DAY) return `${Math.floor(delta / HOUR)}h`
  if (delta < 7 * DAY) return WEEKDAY.format(new Date(ms))
  return shortDate(iso)
}

const DATE = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  timeZone: 'UTC',
})
